<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { API_BASE_URL } from '../config'
import MdEditor from '../components/MdEditor.vue'

const props = defineProps<{
  id: string
}>()

const project = ref<any>(null)
const saving = ref(false)
const showEdit = ref(false)
const categories = ref<{ id: number; name: string }[]>([])

// 三区域独立加载状态
const leftLoading = ref(true)
const contentLoading = ref(true)
const rightLoading = ref(true)
const contentSaving = ref(false)

// 词条列表
const termList = ref<{ id: number; text: string; content: string }[]>([])
const termLoading = ref(false)

// Toast提示
const toast = ref({ show: false, message: '', type: 'success' })
let toastTimer: any = null

const showToast = (message: string, type: 'success' | 'error' = 'success') => {
  toast.value = { show: true, message, type }
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = setTimeout(() => {
    toast.value.show = false
  }, 2000)
}

// 确认弹框
const confirmDialog = ref({ show: false, title: '', message: '', onConfirm: () => {} })

const showConfirm = (title: string, message: string, onConfirm: () => void) => {
  confirmDialog.value = { show: true, title, message, onConfirm }
}

const closeConfirm = () => {
  confirmDialog.value.show = false
}

const handleConfirm = () => {
  confirmDialog.value.onConfirm()
  confirmDialog.value.show = false
}

// 编辑表单
const editForm = ref({
  title: '',
  cat_id: 0,
  description: '',
  startup_type: '1',
  content: '',
  market_score: 5,
  product_score: 5,
  tech_score: 5,
  business_score: 5
})

// 评分悬停状态
const hoverMarketScore = ref(0)
const hoverProductScore = ref(0)
const hoverTechScore = ref(0)
const hoverBusinessScore = ref(0)

// 项目描述文本域自适应高度
const descTextareaRef = ref<HTMLTextAreaElement | null>(null)

const autoResizeTextarea = () => {
  const textarea = descTextareaRef.value
  if (textarea) {
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }
}

// 获取项目内容
const fetchProjectContent = async () => {
  contentLoading.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/startup/content?id=${props.id}`)
    const result = await response.json()
    if (result.code === 1 && result.data) {
      editForm.value.content = result.data.content || ''
    }
  } catch (error) {
    console.error('获取项目内容失败:', error)
  } finally {
    contentLoading.value = false
  }
}

// 获取词条列表
const fetchTermList = async () => {
  termLoading.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/startup/term?startup_id=${props.id}`)
    const result = await response.json()
    if (result.code === 1 && result.data) {
      termList.value = result.data
    }
  } catch (error) {
    console.error('获取词条列表失败:', error)
  } finally {
    termLoading.value = false
    rightLoading.value = false
  }
}

// 查看词条详情（触发编辑器抽屉）
const viewTermDetail = (term: { id: number; text: string; content: string }) => {
  // 触发自定义事件，通知 MdEditor 打开抽屉
  window.dispatchEvent(new CustomEvent('open-term-drawer', { 
    detail: { id: term.id.toString(), text: term.text, content: term.content }
  }))
}

// 保存项目内容
const saveProjectContent = async () => {
  contentSaving.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/startup/content/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: props.id,
        content: editForm.value.content
      })
    })
    const result = await response.json()
    if (result.code === 1) {
      showToast('保存成功')
    } else {
      showToast(result.msg || '保存失败', 'error')
    }
  } catch (error) {
    console.error('保存项目内容失败:', error)
    showToast('保存失败，请稍后重试', 'error')
  } finally {
    contentSaving.value = false
  }
}

const goBack = () => {
  window.history.back()
}

const fetchProjectDetail = async () => {
  leftLoading.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/startup/detail?id=${props.id}`)
    const result = await response.json()
    if (result.code === 1) {
      project.value = result.data
      // 初始化编辑表单
      editForm.value = {
        title: result.data.title || '',
        cat_id: result.data.tag_id || 0,
        description: result.data.description || '',
        startup_type: result.data.startup_type?.toString() || '1',
        content: '',
        market_score: result.data.market_score || 5,
        product_score: result.data.product_score || 5,
        tech_score: result.data.tech_score || 5,
        business_score: result.data.business_score || 5
      }
    }
  } catch (error) {
    console.error('获取项目详情失败:', error)
  } finally {
    leftLoading.value = false
  }
}

const getStars = (count: number) => '★'.repeat(count) + '☆'.repeat(10 - count)

// 获取项目类型对应的文字（反向映射）
const getTypeLabel = (type: string): string => {
  const typeMap: Record<string, string> = {
    '1': 'MVP',
    '0': 'PERSEVERE',
    '4': 'PIVOT'
  }
  return typeMap[type] ?? type
}

// 格式化日期
const formatDate = (timestamp: number) => {
  const date = new Date(timestamp * 1000)
  return date.toLocaleDateString('zh-CN')
}

// 编辑项目
const editProject = () => {
  alert('编辑功能待实现')
}

// 删除项目
const deleteProject = () => {
  showConfirm('确认删除', '确定要删除这个项目吗？删除后无法恢复。', async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/startup/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: props.id })
      })
      const result = await response.json()
      if (result.code === 1) {
        showToast('删除成功')
        setTimeout(() => {
          window.history.back()
        }, 1000)
      } else {
        showToast(result.msg || '删除失败', 'error')
      }
    } catch (error) {
      console.error('删除项目失败:', error)
      showToast('删除失败，请稍后重试', 'error')
    }
  })
}

// 保存项目
const saveProject = async () => {
  if (!editForm.value.title.trim()) {
    alert('请输入项目名称')
    return
  }
  saving.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/startup/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: props.id,
        title: editForm.value.title,
        tag_id: editForm.value.cat_id,
        description: editForm.value.description,
        startup_type: parseInt(editForm.value.startup_type),
        market_score: editForm.value.market_score,
        product_score: editForm.value.product_score,
        tech_score: editForm.value.tech_score,
        business_score: editForm.value.business_score
      })
    })
    const result = await response.json()
    if (result.code === 1) {
      // 更新本地数据
      project.value = { ...project.value, ...editForm.value }
      showToast('保存成功')
    } else {
      showToast(result.msg || '保存失败', 'error')
    }
  } catch (error) {
    console.error('保存项目失败:', error)
    alert('保存失败，请稍后重试')
  } finally {
    saving.value = false
  }
}

// 获取分类列表
const fetchCategories = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/startup/tag`)
    const result = await response.json()
    if (result.code === 1) {
      categories.value = result.data.map((tag: any) => ({
        id: tag.id,
        name: tag.name
      }))
    }
  } catch (error) {
    console.error('获取分类失败:', error)
  }
}

onMounted(() => {
  fetchProjectDetail()
  fetchCategories()
  fetchProjectContent()
  fetchTermList()
  
  // 监听词条创建、更新和删除事件
  window.addEventListener('term-created', handleTermCreated as EventListener)
  window.addEventListener('term-updated', handleTermUpdated as EventListener)
  window.addEventListener('term-deleted', handleTermDeleted as EventListener)
})

// 处理词条创建事件
const handleTermCreated = (event: CustomEvent) => {
  // 重新获取词条列表
  fetchTermList()
}

// 处理词条更新事件
const handleTermUpdated = () => {
  // 重新获取词条列表
  fetchTermList()
}

// 处理词条删除事件
const handleTermDeleted = () => {
  // 重新获取词条列表
  fetchTermList()
}
</script>

<template>
  <div class="startup-item-page">
    <div class="detail-layout">
      <!-- 左侧：项目信息（类似 Summary 的日历区域） -->
      <aside class="info-section">
        <!-- 左侧加载状态 -->
        <div v-if="leftLoading" class="section-loading">
          <span class="loading-spinner"></span>
          <span>加载中...</span>
        </div>
        <div v-else-if="project" class="info-card">
          <!-- 项目标题和类型 -->
          <div class="info-header">
            <input 
              v-model="editForm.title" 
              type="text" 
              class="title-input-hidden" 
              placeholder="请输入项目名称"
            />
            <div class="project-meta">
              <span class="meta-tag" :class="project.startup_type">{{ getTypeLabel(project.startup_type) }}</span>
              <span class="meta-date">{{ formatDate(project.create_at) }}</span>
            </div>
          </div>
          
          <!-- 项目描述 -->
          <div class="form-group">
            <textarea 
              ref="descTextareaRef"
              v-model="editForm.description" 
              class="desc-textarea-hidden" 
              placeholder="请输入项目描述"
              @input="autoResizeTextarea"
            ></textarea>
          </div>
          
          <!-- 项目分类 -->
          <div class="form-group">
            <label>项目分类</label>
            <select v-model="editForm.cat_id">
              <option :value="0">请选择分类</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>
          
          <!-- 项目状态 -->
          <div class="form-group">
            <label>项目状态</label>
            <div class="radio-group">
              <label class="radio-item">
                <input type="radio" v-model="editForm.startup_type" value="1" />
                <span class="radio-label">MVP</span>
              </label>
              <label class="radio-item">
                <input type="radio" v-model="editForm.startup_type" value="0" />
                <span class="radio-label">PERSEVERE</span>
              </label>
              <label class="radio-item">
                <input type="radio" v-model="editForm.startup_type" value="4" />
                <span class="radio-label">PIVOT</span>
              </label>
            </div>
          </div>
          
          <!-- 可行性评分 -->
          <div class="form-group">
            <label>可行性评分</label>
            <div class="rating-list">
              <div class="rating-row" @mouseleave="hoverMarketScore = 0">
                <span class="rating-label-full">市场可行性</span>
                <div class="star-rating">
                  <span v-for="n in 10" :key="n" class="star" :class="{ active: n <= (hoverMarketScore || editForm.market_score) }" @mouseenter="hoverMarketScore = n" @click="editForm.market_score = n">★</span>
                </div>
              </div>
              <div class="rating-row" @mouseleave="hoverProductScore = 0">
                <span class="rating-label-full">产品可行性</span>
                <div class="star-rating">
                  <span v-for="n in 10" :key="n" class="star" :class="{ active: n <= (hoverProductScore || editForm.product_score) }" @mouseenter="hoverProductScore = n" @click="editForm.product_score = n">★</span>
                </div>
              </div>
              <div class="rating-row" @mouseleave="hoverTechScore = 0">
                <span class="rating-label-full">技术可行性</span>
                <div class="star-rating">
                  <span v-for="n in 10" :key="n" class="star" :class="{ active: n <= (hoverTechScore || editForm.tech_score) }" @mouseenter="hoverTechScore = n" @click="editForm.tech_score = n">★</span>
                </div>
              </div>
              <div class="rating-row" @mouseleave="hoverBusinessScore = 0">
                <span class="rating-label-full">商业模式</span>
                <div class="star-rating">
                  <span v-for="n in 10" :key="n" class="star" :class="{ active: n <= (hoverBusinessScore || editForm.business_score) }" @mouseenter="hoverBusinessScore = n" @click="editForm.business_score = n">★</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 综合评分 -->
          <div class="total-score">
            <span>综合评分</span>
            <span class="total-value">{{ ((editForm.market_score + editForm.product_score + editForm.tech_score + editForm.business_score) / 4).toFixed(1) }}</span>
          </div>
          
          <!-- 操作按钮 -->
          <div class="action-section">
            <button class="btn-primary" :disabled="saving" @click="saveProject">
              <span v-if="saving" class="btn-loading"></span>
              <span v-else>编辑项目</span>
            </button>
            <button class="btn-danger" @click="deleteProject">删除项目</button>
          </div>
        </div>
      </aside>


      <!-- 右侧：项目内容区 -->
      <main class="content-section">
        <div class="content-layout">
          <!-- 左侧：编辑器 -->
          <div class="editor-area">
            <MdEditor 
              v-model="editForm.content" 
              :saving="contentSaving"
              :loading="contentLoading"
              :noMaxWidth="true"
              @save="saveProjectContent"
            />
          </div>
          
          <!-- 右侧：词条/卡片内容 -->
          <aside class="sidebar-area">
            <!-- 右侧加载状态 -->
            <div v-if="rightLoading" class="section-loading">
              <span class="loading-spinner"></span>
              <span>加载中...</span>
            </div>
            <div v-else class="sidebar-card">
              <h3>相关词条</h3>
              <div v-if="termLoading" class="term-loading">
                <span class="loading-spinner-small"></span>
              </div>
              <div v-else-if="termList.length === 0" class="term-empty">
                暂无词条
              </div>
              <div v-else class="term-list">
                <div 
                  class="term-item" 
                  v-for="(term, index) in termList" 
                  :key="term.id"
                  @click="viewTermDetail(term)"
                >
                  <span class="term-index">{{ index + 1 }}</span>
                  <span class="term-text">{{ term.text }}</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>
      
    </div>
    <!-- 项目不存在状态 -->
    <div v-if="!leftLoading && !project" class="empty-state">
      <div class="empty-icon">📭</div>
      <div class="empty-text">项目不存在</div>
    </div>
  </div>
  
  <!-- Toast提示 -->
  <div v-if="toast.show" class="toast" :class="toast.type">
    {{ toast.message }}
  </div>
  
  <!-- 确认弹框 -->
  <div v-if="confirmDialog.show" class="confirm-overlay" @click="closeConfirm">
    <div class="confirm-dialog" @click.stop>
      <div class="confirm-title">{{ confirmDialog.title }}</div>
      <div class="confirm-message">{{ confirmDialog.message }}</div>
      <div class="confirm-actions">
        <button class="btn-cancel" @click="closeConfirm">取消</button>
        <button class="btn-confirm" @click="handleConfirm">确定</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.startup-item-page {
  display: flex;
  height: 100%;
  background: #f5f5f5;
}

.detail-layout {
  display: flex;
  width: 100%;
  height: 100%;
}

/* 区域加载状态 */
.section-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  gap: 0.75rem;
}

.section-loading .loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f0f0f0;
  border-top-color: #42b883;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.section-loading span {
  font-size: 0.9rem;
  color: #999;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 左侧信息区域（类似 Summary 的日历区域） */
.info-section {
  width: 380px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  padding: 1.5rem;
  flex-shrink: 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.info-section::-webkit-scrollbar {
  display: none;
}

.info-card {
  background: #fff;
  border-radius: 8px;
}

.info-header {
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.title-input-hidden {
  width: 100%;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  border: none;
  padding: 0;
  margin: 0 0 0.75rem 0;
  background: transparent;
  outline: none;
  line-height: 1.4;
}

.title-input-hidden:focus {
  border: none;
  outline: none;
}

.title-input-hidden::placeholder {
  color: #ccc;
}

/* 项目描述隐形文本域 */
.desc-textarea-hidden {
  width: 100%;
  min-height: calc(1 * 1.6rem);
  font-size: 1rem;
  color: #555;
  border: 0;
  padding: 0;
  margin: 0;
  background: transparent;
  outline: 0;
  resize: none;
  line-height: 1.6;
  box-shadow: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  overflow: hidden;
}

.desc-textarea-hidden:focus {
  border: 0;
  outline: 0;
  box-shadow: none;
}

.desc-textarea-hidden::placeholder {
  color: #ccc;
}

.form-group textarea{
   border: 0 !important;
   padding:0 !important;
   margin: 0 !important;
   outline: 0 !important;
}

.project-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.meta-tag {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.meta-tag.MVP {
  background: #e3f2fd;
  color: #1976d2;
}

.meta-tag.PERSEVERE {
  background: #e8f5e9;
  color: #388e3c;
}

.meta-tag.PIVOT {
  background: #fff3e0;
  color: #f57c00;
}

.meta-date {
  font-size: 0.8rem;
  color: #999;
}

/* 评分区域 */
.score-section {
  margin-bottom: 1.5rem;
}

.score-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #666;
  margin: 0 0 1rem 0;
}

.score-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.score-item {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.score-label {
  font-size: 0.8rem;
  color: #666;
}

.score-bar-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.score-bar-bg {
  flex: 1;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.score-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #42b883, #369970);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.score-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #42b883;
  width: 24px;
  text-align: right;
}

.total-score {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
}

.total-score span:first-child {
  font-size: 0.85rem;
  color: #666;
}

.total-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #42b883;
}

/* 操作按钮 */
.action-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.btn-primary {
  width: 100%;
  height: 40px;
  padding: 0 1rem;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-sizing: border-box;
}

.btn-primary:hover:not(:disabled) {
  background: #369970;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-danger {
  width: 100%;
  height: 40px;
  padding: 0 1rem;
  background: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-sizing: border-box;
}

.btn-danger:hover {
  background: #ffebee;
  color: #f44336;
}

/* 右侧内容区域（类似 Summary 的详情区域） */
.content-section {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  background: #fff;
}

.content-layout {
  display: flex;
  gap: 1rem;
  height: 100%;
}

.editor-area {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.sidebar-area {
  width: 280px;
  flex-shrink: 0;
  border-left: 1px solid #e8e8e8;
  padding-left: 1rem;
}

.sidebar-card {
  background: transparent;
  padding: 0.5rem 0;
}

.sidebar-card h3 {
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #666;
}

.term-loading {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.loading-spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-top-color: #42b883;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.term-empty {
  text-align: center;
  padding: 2rem 0;
  color: #bbb;
  font-size: 0.8rem;
}

.term-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.term-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.term-item:last-child {
  border-bottom: none;
}

.term-item:hover {
  background: #fafafa;
}

.term-index {
  font-size: 0.8rem;
  color: #999;
  min-width: 20px;
  text-align: center;
}

.term-text {
  font-size: 0.9rem;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}



.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 0.85rem;
}

.info-item .label {
  color: #666;
}

.info-item .value {
  color: #333;
  font-weight: 500;
}

.info-item .value.status {
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
}

.info-item .value.status.MVP {
  background: #e3f2fd;
  color: #1976d2;
}

.info-item .value.status.PERSEVERE {
  background: #e8f5e9;
  color: #388e3c;
}

.info-item .value.status.PIVOT {
  background: #fff3e0;
  color: #f57c00;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag-item {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #e0e0e0;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #666;
}

.content-section .content-card {
  background: #fff;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 1rem;
}

.content-section .content-card:last-child {
  margin-bottom: 0;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.content-header h2 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.content-body {
  color: #555;
  line-height: 1.8;
  font-size: 0.95rem;
}

.content-body p {
  margin: 0;
}

.empty-text {
  color: #999;
  font-style: italic;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #666;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #42b883;
}

/* 单选框组 */
.radio-group {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: #666;
}

.radio-item input[type="radio"] {
  width: auto;
  margin: 0;
  cursor: pointer;
}

.radio-label {
  cursor: pointer;
}

.ratings-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rating-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.rating-label-full {
  font-size: 0.9rem;
  color: #666;
  white-space: nowrap;
}

.star-rating {
  display: flex;
  gap: 0.25rem;
  cursor: pointer;
  user-select: none;
}

.star {
  font-size: 1.1rem;
  color: #ddd;
  transition: color 0.2s;
}

.star.active {
  color: #ffc107;
}

.star:hover {
  color: #ffc107;
}

.form-actions {
  margin-top: 1.5rem;
}

.save-btn {
  width: 100%;
  padding: 0.75rem;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background 0.2s;
}

.save-btn:hover:not(:disabled) {
  background: #369970;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-loading {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 1rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1rem;
}

.info-label {
  font-size: 0.8rem;
  color: #999;
}

.info-value {
  font-size: 0.95rem;
  color: #333;
  font-weight: 500;
}

.project-scores {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
}

.score-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.score-label {
  font-size: 0.85rem;
  color: #666;
}

.score-stars {
  font-size: 0.9rem;
  color: #ffc107;
  letter-spacing: 1px;
}

.project-desc {
  color: #666;
  line-height: 1.8;
  margin-bottom: 1.5rem;
}

.project-content {
  color: #333;
  line-height: 1.8;
}

.project-content p {
  margin: 0.5rem 0;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.action-btn {
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.action-btn.edit {
  background: #42b883;
  color: #fff;
}

.action-btn.edit:hover {
  background: #369970;
}

.action-btn.delete {
  background: #f5f5f5;
  color: #666;
}

.action-btn.delete:hover {
  background: #ffebee;
  color: #f44336;
}

.stats-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-label {
  font-size: 0.85rem;
  color: #666;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: 600;
  color: #42b883;
}

.side-info {
  padding: 1rem 1.25rem;
}

.side-info-grid {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
}

.side-info-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.side-label {
  font-size: 0.8rem;
  color: #999;
}

.side-value {
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
}

.side-score {
  font-size: 1rem;
  font-weight: 600;
  color: #42b883;
}

.side-info-item.action {
  margin-left: auto;
}

.delete-link {
  background: none;
  border: none;
  color: #999;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  transition: color 0.2s;
}

.delete-link:hover {
  color: #f44336;
}

.page-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem;
  gap: 1.5rem;
  color: #999;
}

.loading-spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top-color: #42b883;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.project-type {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.project-type.MVP {
  background: #e3f2fd;
  color: #1976d2;
}

.project-type.PERSEVERE {
  background: #e8f5e9;
  color: #388e3c;
}

.project-type.PIVOT {
  background: #fff3e0;
  color: #f57c00;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem 2rem;
  gap: 1rem;
}

.empty-icon {
  font-size: 4rem;
  opacity: 0.5;
}

.empty-text {
  font-size: 0.9rem;
  color: #999;
}

/* Toast提示 */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

.toast.success {
  background: #42b883;
  color: #fff;
}

.toast.error {
  background: #ff6b6b;
  color: #fff;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* 确认弹框 */
.confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.confirm-dialog {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  width: 90%;
  max-width: 360px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: scaleIn 0.2s ease;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.confirm-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
}

.confirm-message {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.confirm-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.confirm-actions button {
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.btn-cancel:hover {
  background: #e8e8e8;
}

.btn-confirm {
  background: #ff6b6b;
  color: #fff;
}

.btn-confirm:hover {
  background: #ff5252;
}
</style>
